<script setup lang="ts">
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import PrimaryButton from "@/views/blocks/shared/section/buttons/primary-button/PrimaryButton.vue";
import SecondaryButton from "@/views/blocks/shared/section/buttons/secondary-button/SecondaryButton.vue";
import EditableElement from "@/views/page/EditableElement.vue";
import type { SimpleCenteredProps } from "./cta.interfaces";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import { computed } from "vue";
import type { ScrollAnimationType } from "@/apps/page/page-project.interface";

const props = defineProps<{
    block: PageBlock<SimpleCenteredProps>;
    pageColors: CssVarColors;
    scrollAnimationType?: ScrollAnimationType;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section
        :id="block.webId"
        :background-color="styleValues.background"
        :scrollAnimationType="scrollAnimationType"
    >
        <FullSpacing>
            <div class="max-w-2xl lg:max-w-7xl mx-auto">
                <div class="max-w-2xl mx-auto text-center flex flex-col gap-y-4">
                    <EditableElement
                        v-if="!dataValues.tag.isHidden"
                        :blockId="block.id"
                        field-name="tag"
                    >
                        <p :style="{ color: styleValues.brand }" class="font-semibold">
                            {{ dataValues.tag.data.text }}
                        </p>
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        field-name="title"
                    >
                        <Heading
                            :tag="dataValues.title.data.tag"
                            :text="dataValues.title.data.text"
                            :text-color="styleValues.text"
                            class="text-4xl sm:text-5xl"
                        />
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.description.isHidden"
                        :blockId="block.id"
                        field-name="description"
                    >
                        <Paragraph
                            :text="dataValues.description.data.text"
                            :text-color="styleValues.text"
                            class="text-lg/8 opacity-70"
                        />
                    </EditableElement>

                    <div
                        v-if="
                            !dataValues.primaryButton.isHidden ||
                            !dataValues.secondaryButton.isHidden
                        "
                        class="flex flex-wrap items-center justify-center gap-6"
                        :class="{
                            'mt-6':
                                !dataValues.tag.isHidden ||
                                !dataValues.title.isHidden ||
                                !dataValues.description.isHidden,
                        }"
                    >
                        <EditableElement
                            v-if="!dataValues.primaryButton.isHidden"
                            :blockId="block.id"
                            field-name="primaryButton"
                        >
                            <PrimaryButton
                                :label="dataValues.primaryButton.data.label"
                                :href="dataValues.primaryButton.data.href"
                                :text-color="styleValues.background"
                                :background-color="styleValues.brand"
                                class="px-3.5 py-2.5 rounded-lg text-sm"
                            />
                        </EditableElement>

                        <EditableElement
                            v-if="!dataValues.secondaryButton.isHidden"
                            :blockId="block.id"
                            field-name="secondaryButton"
                        >
                            <SecondaryButton
                                :label="dataValues.secondaryButton.data.label"
                                :href="dataValues.secondaryButton.data.href"
                                :text-color="styleValues.text"
                                class="text-sm"
                            />
                        </EditableElement>
                    </div>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>
